import React, { useEffect, useRef, useState } from "react";

export type PickerOption = {
  id: string;
  name: string;
};

type Props = {
  ariaLabel: string;
  options: PickerOption[];
  /** Current snapshot value (an id, as string). Empty string = no pick. */
  selectedId: string;
  onSelect: (id: string) => void;
};

/**
 * CollapsingOptionPicker — mobile tappable list that COLLAPSES after a
 * pick so the quantity input below it stays visible without scrolling.
 *
 * States:
 *  - No selection yet: list is expanded so the user can pick.
 *  - After a pick: list collapses to a single "selected summary" row
 *    with a ✓ and a chevron. Tapping the summary reopens the list.
 *
 * Externally-driven changes to selectedId (e.g., a parent that resets
 * the value) collapse the list too — keeps the UI consistent with the
 * current snapshot.
 *
 * Deliberately does NOT auto-focus or scroll the qty field after a
 * pick. We tried both (synchronous focus + visualViewport-clamped
 * scrollBy) and either competed with iOS's native focus-scroll or
 * overshot the viewport. The user taps the qty field themselves —
 * predictable, no jank.
 */
export default function CollapsingOptionPicker({
  ariaLabel,
  options,
  selectedId,
  onSelect,
}: Props) {
  const [listOpen, setListOpen] = useState<boolean>(() => !selectedId);
  const lastIdRef = useRef<string>(selectedId);

  useEffect(() => {
    if (selectedId !== lastIdRef.current) {
      lastIdRef.current = selectedId;
      setListOpen(false);
    }
  }, [selectedId]);

  const selected = options.find(
    (o) => String(o.id) === String(selectedId)
  );

  if (!listOpen && selected) {
    return (
      <button
        type="button"
        className="m-picker-summary"
        onClick={() => setListOpen(true)}
        aria-label={`Change ${ariaLabel}, currently ${selected.name}`}
      >
        <span className="m-picker-check" aria-hidden>
          ✓
        </span>
        <span className="m-picker-summary-name">{selected.name}</span>
        <span className="m-picker-caret" aria-hidden>
          ▾
        </span>
      </button>
    );
  }

  return (
    <div className="m-option-list" role="radiogroup" aria-label={ariaLabel}>
      {options.map((opt) => {
        const idStr = String(opt.id);
        const isSelected = idStr === String(selectedId);
        return (
          <button
            type="button"
            key={idStr}
            role="radio"
            aria-checked={isSelected}
            className={`m-option-row${isSelected ? " is-selected" : ""}`}
            onClick={() => {
              onSelect(idStr);
              setListOpen(false);
            }}
          >
            <span className="m-option-check" aria-hidden>
              {isSelected ? "✓" : ""}
            </span>
            <span className="m-option-name">{opt.name}</span>
          </button>
        );
      })}
    </div>
  );
}
